import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      dropdown: [
        { name: 'IT Infrastructure Management', path: '/services/it-infrastructure' },
        { name: 'Cybersecurity & Compliance', path: '/services/cybersecurity' },
        { name: 'Cloud Computing & SaaS', path: '/services/cloud-services' },
        { name: 'Business Continuity & DR', path: '/services/business-continuity' },
        { name: 'VoIP & Unified Communications', path: '/services/voip' },
        { name: 'Audio Visual Solutions', path: '/services/audio-visual' },
        { name: 'Video Surveillance', path: '/services/video-surveillance' },
        { name: 'Access Control Systems', path: '/services/access-control' },
        { name: 'Intrusion Detection & Alarms', path: '/services/intrusion-alarms' },
        { name: 'Structured Cabling', path: '/services/structured-cabling' },
        { name: 'vCIO Services', path: '/services/vcio' },
      ]
    },
    { name: 'Process', path: '/process' },
    { name: 'Industries', path: '/industries' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav 
      data-testid="navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-md' 
          : 'bg-white'
      }`}
    >
      {/* Top bar */}
      <div className="bg-mimo-primary text-white py-2 hidden md:block">
        <div className="container-custom flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <span>Serving Tri-State Area & Nationwide</span>
            <span>24/7 Support Available</span>
          </div>
          <a 
            href="tel:201-473-4443" 
            className="flex items-center gap-2 hover:text-mimo-accent-light transition-colors"
            data-testid="header-phone"
          >
            <Phone className="w-4 h-4" />
            <span className="font-semibold">201-473-4443</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="container-custom">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/rjkxg9wt_5.png" 
              alt="mimoIT Technology Solutions" 
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.dropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                        isActive(link.path)
                          ? 'text-mimo-primary'
                          : 'text-slate-700 hover:text-mimo-primary'
                      }`}
                      data-testid={`nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4" />
                    </Link>
                    {servicesOpen && (
                      <div className="absolute top-full left-0 pt-2 w-56">
                        <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2 animate-scale-in">
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.name}
                              to={item.path}
                              className="block px-4 py-2 text-sm text-slate-700 hover:bg-mimo-surface hover:text-mimo-primary transition-colors"
                              data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={`text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-mimo-primary'
                        : 'text-slate-700 hover:text-mimo-primary'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="tel:201-473-4443" 
              className="flex items-center gap-2 text-mimo-primary font-semibold hover:text-mimo-secondary transition-colors"
              data-testid="nav-phone-cta"
            >
              <Phone className="w-5 h-5" />
              <span>201-473-4443</span>
            </a>
            <Link 
              to="/contact" 
              className="btn-primary text-sm"
              data-testid="nav-consultation-btn"
            >
              Request Consultation
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100 animate-fade-in">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.path}
                    className={`block py-2 text-base font-medium ${
                      isActive(link.path)
                        ? 'text-mimo-primary'
                        : 'text-slate-700'
                    }`}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                  {link.dropdown && (
                    <div className="pl-4 mt-2 space-y-2">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block py-1 text-sm text-slate-600 hover:text-mimo-primary"
                          onClick={() => setIsOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a 
                href="tel:201-473-4443" 
                className="flex items-center gap-2 py-2 text-mimo-primary font-semibold"
                data-testid="mobile-phone-cta"
              >
                <Phone className="w-5 h-5" />
                <span>201-473-4443</span>
              </a>
              <Link 
                to="/contact" 
                className="btn-primary text-center mt-2"
                onClick={() => setIsOpen(false)}
                data-testid="mobile-consultation-btn"
              >
                Request Consultation
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;